/**
 * @file routes/_app/route.tsx
 * @description 認證保護佈局路由。
 *
 * 職責：
 * - 所有 _app/* 子路由均在此路由保護下（需登入才能訪問）
 * - beforeLoad 在路由加載前檢查 AuthStore，未登入則嘗試用 cookie 還原（GET /api/auth/me），
 *   仍失敗才跳轉至 /login
 * - 包裹 AppShell（Sidebar + Header），為所有後台頁面提供一致的佈局
 *
 * TanStack Router 命名約定：
 * - 以 _ 開頭的段為「路徑無關佈局路由」，不影響 URL，僅提供組件包裹
 */

import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { getPersistedAuthUser, useAuthStore, type AuthUser } from '@/lib/auth-context';
import { AppShell } from '@/components/layout/AppShell';
import { apiClient, ApiError } from '@/lib/api-client';

/**
 * _app 路由定義。
 * - beforeLoad：先看 store；store 空時嘗試用 httpOnly cookie 打 /api/auth/me 還原 session；
 *   仍失敗才 redirect 到 /login（避免硬刷新後因 store 重置誤踢）
 * - component：使用 AppShell 包裹子頁面的 Outlet
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async () => {
    // 1) 已 persist 的 user 直接放行（樂觀路徑：刷新後 zustand 從 localStorage 還原）
    let user = useAuthStore.getState().user ?? getPersistedAuthUser();
    if (user && !useAuthStore.getState().user) {
      useAuthStore.getState().setUser(user);
    }

    // 2) 沒有 user 時，嘗試用 cookie 打 /me 還原；只在 store 真的空時才額外發請求，
    //    避免每次路由切換都打網路。
    if (!user) {
      try {
        const r = await apiClient.get<{ user: AuthUser }>('/api/auth/me');
        useAuthStore.getState().setUser(r.user);
        user = r.user;
      } catch (err) {
        // 非 401 的錯誤（網路掛、500 …）不應該誤跳 login，僅記錄、繼續走下一步
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error('[auth/_app.beforeLoad] /api/auth/me failed', err);
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (!user) throw redirect({ to: '/login' });
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});
